import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Phone, MapPin, ChevronDown, Check, SkipForward } from 'lucide-react';

const s = {
  page: { maxWidth: 900 },
  h1: { fontSize: 22, fontWeight: 700, color: '#212529', marginBottom: 4 },
  sub: { fontSize: 13, color: '#6c757d', marginBottom: 20 },
  progress: { background: '#e9ecef', borderRadius: 8, height: 8, marginBottom: 24, overflow: 'hidden' },
  progressBar: { height: '100%', background: 'linear-gradient(135deg,#7c6af5,#c084fc)', borderRadius: 8, transition: 'width .4s' },
  card: { background: '#ffffff', border: '1px solid #e9ecef', borderRadius: 12, padding: 20, marginBottom: 14 },
  cardDone: { background: '#ffffff', border: '1px solid #22c55e40', borderRadius: 12, padding: 20, marginBottom: 14, opacity: 0.6 },
  cardSkip: { background: '#ffffff', border: '1px solid #f59e0b40', borderRadius: 12, padding: 20, marginBottom: 14, opacity: 0.6 },
  row: { display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' },
  nameRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  name: { fontSize: 15, fontWeight: 700, color: '#212529' },
  badge: { fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600 },
  orderSum: { fontSize: 12, color: '#6c757d', marginBottom: 14 },
  label: { fontSize: 11, color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4, display: 'block' },
  input: { background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 8, padding: '8px 12px', color: '#212529', fontSize: 13, width: '100%', boxSizing: 'border-box' },
  select: { background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 8, padding: '8px 12px', color: '#212529', fontSize: 13, width: '100%', boxSizing: 'border-box' },
  field: { flex: 1, minWidth: 140 },
  btnSave: { display: 'flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,#7c6af5,#c084fc)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  btnSkip: { display: 'flex', alignItems: 'center', gap: 6, background: '#e9ecef', color: '#f59e0b', border: '1px solid #f59e0b40', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' },
  btnRow: { display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' },
  doneBadge: { display: 'flex', alignItems: 'center', gap: 6, color: '#22c55e', fontSize: 13, fontWeight: 600 },
  skipBadge: { display: 'flex', alignItems: 'center', gap: 6, color: '#f59e0b', fontSize: 13, fontWeight: 600 },
  empty: { text: { textAlign: 'center', color: '#6c757d', padding: 40, fontSize: 14 } },
  sessionSel: { background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 8, padding: '8px 12px', color: '#212529', fontSize: 13, marginBottom: 20, width: '100%', maxWidth: 360 },
};

const RANK_STYLE = {
  silver:   { background: '#f1f5f920', color: '#94a3b8' },
  gold:     { background: '#fef08a20', color: '#ca8a04' },
  platinum: { background: '#e879f920', color: '#c026d3' },
  normal:   { background: '#e9ecef',   color: '#6c757d' },
};

export default function PostLive({ sessionId: propSessionId }) {
  const [searchParams] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(propSessionId || searchParams.get('session') || '');
  const [customers, setCustomers] = useState([]);
  const [states, setStates] = useState({}); // per customer: { phone, phone2, location_type, province, address, status }
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { if (sessionId) loadCustomers(sessionId); }, [sessionId]);

  async function loadSessions() {
    try {
      const res = await api.get('/live/sessions');
      setSessions(res.data.sessions || []);
    } catch {}
  }

  async function loadCustomers(sid) {
    setLoading(true);
    try {
      const res = await api.get(`/postlive/session/${sid}/no-contact`);
      const custs = res.data.customers || [];
      setCustomers(custs);
      const init = {};
      custs.forEach(c => {
        init[c.customer_id] = {
          phone: c.phone || '',
          phone2: c.phone2 || '',
          location_type: c.location_type === 'unknown' ? '' : (c.location_type || ''),
          province: c.province || '',
          address: c.address || '',
          status: 'pending' // pending | done | skipped
        };
      });
      setStates(init);
    } catch {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }

  function update(customerId, field, value) {
    setStates(p => ({ ...p, [customerId]: { ...p[customerId], [field]: value } }));
  }

  async function save(customer) {
    const st = states[customer.customer_id];
    if (!st.phone) { toast.error('Phone is required'); return; }
    if (!st.location_type) { toast.error('Select PP or Province'); return; }
    try {
      await api.post('/postlive/fill-contact', {
        customer_id: customer.customer_id,
        phone: st.phone,
        phone2: st.phone2,
        location_type: st.location_type,
        province: st.province,
        address: st.address,
        session_id: parseInt(sessionId)
      });
      setStates(p => ({ ...p, [customer.customer_id]: { ...p[customer.customer_id], status: 'done' } }));
      toast.success(`✅ ${customer.name} saved`);
    } catch {
      toast.error('Failed to save');
    }
  }

  function skip(customerId) {
    setStates(p => ({ ...p, [customerId]: { ...p[customerId], status: 'skipped' } }));
  }

  const done = Object.values(states).filter(s => s.status === 'done').length;
  const total = customers.length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div style={s.page}>
      <div style={s.h1}>📋 Post-Live Contact Fill</div>
      <div style={s.sub}>Fill in customer phone and address after the live session</div>

      {/* Session selector (shown when used as standalone page) */}
      {!propSessionId && (
        <select style={s.sessionSel} value={sessionId} onChange={e => setSessionId(e.target.value)}>
          <option value="">— Select a session —</option>
          {sessions.map(s => (
            <option key={s.id} value={s.id}>{s.session_code} · {s.title} · {s.status}</option>
          ))}
        </select>
      )}

      {sessionId && (
        <>
          {/* Progress */}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6c757d', marginBottom: 6 }}>
            <span>{done} / {total} filled</span>
            <span>{pct}%</span>
          </div>
          <div style={s.progress}>
            <div style={{ ...s.progressBar, width: `${pct}%` }} />
          </div>

          {loading && <div style={{ color: '#6c757d', textAlign: 'center', padding: 40 }}>Loading...</div>}

          {!loading && customers.length === 0 && (
            <div style={{ textAlign: 'center', color: '#6c757d', padding: 40, fontSize: 14 }}>
              🎉 No no-contact customers for this session
            </div>
          )}

          {customers.map(c => {
            const st = states[c.customer_id] || {};
            const rank = c.ranking || 'normal';
            return (
              <div key={c.customer_id} style={st.status === 'done' ? s.cardDone : st.status === 'skipped' ? s.cardSkip : s.card}>
                {/* Header */}
                <div style={s.nameRow}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={s.name}>{c.name}</span>
                    {rank !== 'normal' && (
                      <span style={{ ...s.badge, ...RANK_STYLE[rank] }}>
                        {rank === 'silver' ? '🥈' : rank === 'gold' ? '🥇' : '💎'} {rank}
                      </span>
                    )}
                  </div>
                  {st.status === 'done' && <span style={s.doneBadge}><Check size={14} /> Saved</span>}
                  {st.status === 'skipped' && <span style={s.skipBadge}><SkipForward size={14} /> Skipped</span>}
                </div>

                {/* Order summary */}
                <div style={s.orderSum}>
                  {c.orders.map(o => (
                    <span key={o.order_id} style={{ marginRight: 12 }}>
                      {o.items.map(i => `Code ${i.code} x${i.qty}`).join(', ')} · <strong style={{ color: '#a78bf8' }}>${o.total.toFixed(2)}</strong>
                    </span>
                  ))}
                  <span style={{ color: '#212529', fontWeight: 700 }}>Total: ${c.total.toFixed(2)}</span>
                </div>

                {st.status === 'pending' && (
                  <>
                    <div style={s.row}>
                      {/* Phone */}
                      <div style={s.field}>
                        <label style={s.label}><Phone size={10} style={{ marginRight: 4 }} />Phone</label>
                        <input style={s.input} value={st.phone} onChange={e => update(c.customer_id, 'phone', e.target.value)} placeholder="0xx xxx xxx" />
                      </div>
                      {/* Phone 2 */}
                      <div style={s.field}>
                        <label style={s.label}>Phone 2 (optional)</label>
                        <input style={s.input} value={st.phone2} onChange={e => update(c.customer_id, 'phone2', e.target.value)} placeholder="0xx xxx xxx" />
                      </div>
                      {/* Location */}
                      <div style={s.field}>
                        <label style={s.label}><MapPin size={10} style={{ marginRight: 4 }} />Location</label>
                        <select style={s.select} value={st.location_type} onChange={e => update(c.customer_id, 'location_type', e.target.value)}>
                          <option value="">— Select —</option>
                          <option value="phnom_penh">📍 Phnom Penh (COD)</option>
                          <option value="province">🗺️ Province (Pay first)</option>
                        </select>
                      </div>
                    </div>
                    <div style={{ ...s.row, marginTop: 10 }}>
                      {/* Province name */}
                      {st.location_type === 'province' && (
                        <div style={s.field}>
                          <label style={s.label}>Province Name</label>
                          <input style={s.input} value={st.province} onChange={e => update(c.customer_id, 'province', e.target.value)} placeholder="e.g. Siem Reap" />
                        </div>
                      )}
                      {/* Address */}
                      <div style={{ ...s.field, flex: 2 }}>
                        <label style={s.label}>Address Detail</label>
                        <input style={s.input} value={st.address} onChange={e => update(c.customer_id, 'address', e.target.value)} placeholder="Street, village, district..." />
                      </div>
                    </div>
                    <div style={s.btnRow}>
                      <button style={s.btnSkip} onClick={() => skip(c.customer_id)}>
                        <SkipForward size={13} /> Skip
                      </button>
                      <button style={s.btnSave} onClick={() => save(c)}>
                        <Check size={13} /> Save
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
